import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getContainerCategories } from '@/api/containerCategoriesAPI'
import { getCountries } from '@/api/countriesAPI'
import {
  getProviders,
  getManagers,
  getTransporters,
  getStorages,
} from '@/api/providersAPI'
import { getOriginDestiny } from '@/api/originDestinyAPI'
import { getSuppliers } from '@/api/providersAPI'
import { getCurrencies } from '@/api/currenciesAPI'
import { getCostCategories } from '@/api/costCategoriesAPI'
import { getUsers } from '@/api/usersAPI'
import { getProducts } from '@/api/productsAPI'
import { getVehicles } from '@/api/vehiclesAPI'
import type { SearchableSelectOption } from '@/components/newRedesign/components/SearchableSelect'

// Types
interface VehicleFullInfo {
  id: number
  name: string
  identification?: string
  oficialNumber?: string
  type?: string
  maxWeight?: number
  maxHeight?: number
  operatedById?: number
}

// Cache configuration - 15 minutes stale time, 30 minutes garbage collection
const DEFAULT_STALE_TIME = 15 * 60 * 1000 // 15 minutes
const DEFAULT_GC_TIME = 30 * 60 * 1000 // 30 minutes

// ==================== CONTAINER CATEGORIES ====================

/**
 * Hook to fetch container categories with caching
 */
export function useContainerCategories(search?: string) {
  return useQuery({
    queryKey: ['container-categories', search],
    queryFn: async () => {
      const categories = await getContainerCategories(
        search ? { search } : undefined
      )
      return categories.map((c) => ({ id: c.id, label: c.name }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for container categories compatible with SearchableSelect
 */
export function useCategoriesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['container-categories', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const categories = await getContainerCategories(
        search ? { search } : undefined
      )
      const result = categories.map((c) => ({ id: c.id, label: c.name }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== COUNTRIES ====================

/**
 * Hook to fetch countries with caching
 */
export function useCountries(search?: string) {
  return useQuery({
    queryKey: ['countries', search],
    queryFn: async () => {
      const countries = await getCountries(search ? { search } : undefined)
      return countries.map((c) => ({ id: c.id, label: c.name }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for countries compatible with SearchableSelect
 */
export function useCountriesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['countries', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const countries = await getCountries(search ? { search } : undefined)
      const result = countries.map((c) => ({ id: c.id, label: c.name }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== PROVIDER COMPANIES ====================

/**
 * Hook to fetch provider companies with caching
 */
export function useProviderCompanies(search?: string) {
  return useQuery({
    queryKey: ['provider-companies', search],
    queryFn: async () => {
      const response = await getProviders({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((company) => ({
        id: company.id,
        label: company.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for provider companies compatible with SearchableSelect
 */
export function useProviderCompaniesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['provider-companies', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getProviders({ page: 1, per_page: 50, search })
      if (!Array.isArray(response)) return []

      const result = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== MANAGER COMPANIES ====================

/**
 * Hook to fetch manager companies with caching
 */
export function useManagerCompanies(search?: string) {
  return useQuery({
    queryKey: ['manager-companies', search],
    queryFn: async () => {
      const response = await getManagers({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((company) => ({
        id: company.id,
        label: company.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for manager companies compatible with SearchableSelect
 */
export function useManagerCompaniesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['manager-companies', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getManagers({ page: 1, per_page: 50, search })
      if (!Array.isArray(response)) return []

      const result = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== TRANSPORTER COMPANIES ====================

/**
 * Hook to fetch transporter companies with caching
 */
export function useTransporterCompanies(search?: string) {
  return useQuery({
    queryKey: ['transporter-companies', search],
    queryFn: async () => {
      const response = await getTransporters({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((company) => ({
        id: company.id,
        label: company.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for transporter companies compatible with SearchableSelect
 */
export function useTransporterCompaniesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['transporter-companies', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getTransporters({ page: 1, per_page: 50, search })
      if (!Array.isArray(response)) return []

      const result = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== STORAGE COMPANIES ====================

/**
 * Hook to fetch storage companies with caching
 */
export function useStorageCompanies(search?: string) {
  return useQuery({
    queryKey: ['storage-companies', search],
    queryFn: async () => {
      const response = await getStorages({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((company) => ({
        id: company.id,
        label: company.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for storage companies compatible with SearchableSelect
 */
export function useStorageCompaniesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['storage-companies', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getStorages({ page: 1, per_page: 50, search })
      if (!Array.isArray(response)) return []

      const result = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== ORIGIN/DESTINATION ====================

/**
 * Hook to fetch origin/destination locations with caching
 */
export function useOriginDestiny(search?: string) {
  return useQuery({
    queryKey: ['originDestiny', search],
    queryFn: async () => {
      const response = await getOriginDestiny({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((item) => ({
        id: item.id,
        label: item.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for origins compatible with SearchableSelect
 */
export function useOriginsFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['originDestiny', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getOriginDestiny({ page: 1, per_page: 50, search })
      if (!Array.isArray(response)) return []

      const result = response.map((item) => ({
        id: item.id,
        label: item.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

/**
 * Fetcher for destinations compatible with SearchableSelect
 */
export function useDestinationsFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['originDestiny', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getOriginDestiny({ page: 1, per_page: 50, search })
      if (!Array.isArray(response)) return []

      const result = response.map((item) => ({
        id: item.id,
        label: item.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== SUPPLIERS ====================

/**
 * Hook to fetch suppliers with caching
 */
export function useSuppliers(search?: string) {
  return useQuery({
    queryKey: ['suppliers', search],
    queryFn: async () => {
      const response = await getSuppliers({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((supplier: { id: number; name: string }) => ({
        id: supplier.id,
        label: supplier.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for suppliers compatible with SearchableSelect
 */
export function useSuppliersFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['suppliers', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getSuppliers({ page: 1, per_page: 10, search })
      if (!Array.isArray(response)) return []

      const result = response.map((supplier: { id: number; name: string }) => ({
        id: supplier.id,
        label: supplier.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== CURRENCIES ====================

/**
 * Hook to fetch currencies with caching
 */
export function useCurrencies() {
  return useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

// ==================== FIXED COST CATEGORIES ====================

/**
 * Hook to fetch fixed cost categories with caching
 */
export function useFixedCostCategories(search?: string) {
  return useQuery({
    queryKey: ['fixed-cost-categories', search],
    queryFn: async () => {
      const response = await getCostCategories(search ? { search } : undefined)
      if (!Array.isArray(response)) return []
      return response.map((category) => ({
        id: category.id,
        label: category.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for fixed cost categories compatible with SearchableSelect
 */
export function useFixedCostCategoriesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['fixed-cost-categories', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getCostCategories(search ? { search } : undefined)
      if (!Array.isArray(response)) return []

      const result = response.map((category) => ({
        id: category.id,
        label: category.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== USERS ====================

/**
 * Fetcher for users compatible with SearchableSelect
 */
export function useUsersFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['users', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getUsers({
        page: 1,
        per_page: 30,
        search: search || '',
      })

      if (!Array.isArray(response)) return []

      const result = response.map(
        (user: { id: number; displayName: string; email?: string }) => ({
          id: String(user.id),
          label: `${user.displayName}${user.email ? ` - ${user.email}` : ''}`,
        })
      )

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

// ==================== VEHICLES ====================

/**
 * Fetcher for vehicles compatible with SearchableSelect
 */
export function useVehiclesFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['vehicles', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const items = await getVehicles({
        page: 1,
        per_page: 30,
        search: search || '',
      })

      if (!Array.isArray(items)) return []

      const result = items.map(
        (vehicle: {
          id: number
          name: string
          identification?: string
          oficialNumber?: string
        }) => {
          const identification = vehicle.identification
            ? ` - ${vehicle.identification}`
            : ''
          const oficialNumber = vehicle.oficialNumber
            ? ` (${vehicle.oficialNumber})`
            : ''

          return {
            id: String(vehicle.id),
            label: `${vehicle.name}${identification}${oficialNumber}`,
          }
        }
      )

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}

/**
 * Hook to fetch complete vehicle information for detailed views
 */
export function useVehicleFullInfo(vehicleId: string | null) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ['vehicle-full-info', vehicleId],
    queryFn: async () => {
      if (!vehicleId) return null

      const vehiclesSearchCache = queryClient.getQueryData<VehicleFullInfo[]>([
        'vehicles-full-data',
        '',
      ])

      if (vehiclesSearchCache) {
        const cached = vehiclesSearchCache.find(
          (v) => String(v.id) === vehicleId
        )
        if (cached) return cached
      }

      const items = await getVehicles({
        page: 1,
        per_page: 50,
        search: '',
      })

      queryClient.setQueryData(['vehicles-full-data', ''], items, {
        updatedAt: Date.now(),
      })

      const vehicle = items.find(
        (v: VehicleFullInfo) => String(v.id) === vehicleId
      )
      return vehicle || null
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: !!vehicleId,
  })
}

// ==================== PRODUCTS ====================

/**
 * Hook to fetch products with caching
 */
export function useProducts(search?: string) {
  return useQuery({
    queryKey: ['products', search],
    queryFn: async () => {
      const response = await getProducts({
        page: 1,
        per_page: 50,
        search: search || '',
      })
      if (!Array.isArray(response)) return []
      return response.map((product) => ({
        id: product.id,
        label: product.name,
      }))
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  })
}

/**
 * Fetcher for products compatible with SearchableSelect
 */
export function useProductsFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectOption[]> => {
      const queryKey = ['products', search]
      const cached =
        queryClient.getQueryData<SearchableSelectOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const response = await getProducts({
        page: 1,
        per_page: 50,
        search: search || '',
      })

      if (!Array.isArray(response)) return []

      const result = response.map((product: { id: number; name: string }) => ({
        id: product.id,
        label: product.name,
      }))

      queryClient.setQueryData(queryKey, result, {
        updatedAt: Date.now(),
      })

      return result
    },
    [queryClient]
  )
}
